import { DangerButton } from '@shared/components/buttons';
import { useState } from 'react';
import { useAsyncAction } from '../../../hooks/use-async-action';
import type { ReceivingService } from '../../../services/receiving';
import { StepHeading } from './step-heading';

type OverwriteDetectedProps = {
  storeId: string;
  sessionId: string;
  service: ReceivingService;
  readOnly: boolean;
};

/**
 * Overwrite-detected screen (spec §10 — failed_from=waiting_for_lpos).
 *
 * LPOS replaced our already-written file with its own receiving (post-write
 * overwrite via rcv_file_hash_mismatch). We must NOT offer Retry — re-sending
 * would clobber the receiving LiquorPOS is now working on. Only Abandon.
 */
export function OverwriteDetected({
  storeId,
  sessionId,
  service,
  readOnly,
}: OverwriteDetectedProps) {
  const [abandonError, setAbandonError] = useState<string | null>(null);
  const { pending: abandoning, run: handleAbandon } = useAsyncAction(
    () => service.abandon(storeId, sessionId),
    { errorLabel: 'receiving: abandon failed', setError: setAbandonError, disabled: readOnly }
  );

  return (
    <div className="flex flex-col gap-4" data-testid="overwrite-detected">
      <StepHeading>
        Someone started a receiving in LiquorPOS before it could import yours.
      </StepHeading>
      <p className="text-base leading-relaxed text-gray-700">
        Your receiving file was replaced by a receiving already in progress at the register.
        Retrying would overwrite that receiving.
      </p>
      <p className="text-sm font-medium text-amber-800 bg-amber-50 rounded-md px-3 py-2">
        Click Abandon to clear your receiving. LiquorPOS may already have imported it. Check
        LiquorPOS before starting over.
      </p>
      {abandonError && (
        <p className="text-sm font-medium text-red-700" data-testid="overwrite-abandon-error">
          {abandonError}
        </p>
      )}
      <div className="flex items-center justify-end gap-3 mt-2">
        <DangerButton onClick={handleAbandon} disabled={readOnly || abandoning}>
          {abandoning ? 'Abandoning…' : 'Abandon'}
        </DangerButton>
      </div>
    </div>
  );
}
